<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <HyperspeedBackground class="absolute inset-0 z-0" :beam-count="120" color-scheme="rainbow" :speed-factor="1.5" />
    <div
      class="max-w-md w-full bg-white/80 backdrop-blur-xl p-8 pt-10 pb-6 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
      />

      <h1 class="text-center text-2xl font-bold text-gray-900">{{ greeting }}</h1>
      <p class="text-center text-sm text-gray-500 mt-2 leading-relaxed">
        Enter your email and we'll send you a one-time link&mdash;<br class="hidden sm:inline" />no password to remember.
      </p>

      <div class="mt-6 max-w-[22rem] mx-auto space-y-5">
        <template v-if="!magicLinkSent">
          <div class="space-y-2">
            <label for="magic-link-email" class="text-sm font-medium text-gray-700 block">Email</label>
            <Input
              id="magic-link-email"
              type="email"
              autocomplete="email"
              placeholder="name@example.com"
              v-model="magicLinkEmail"
              class="border-[1px] border-gray-300 rounded-lg p-2.5 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-900"
            />
            <Button
              type="button"
              :disabled="isSendingMagicLink || !magicLinkEmail.trim()"
              @click="sendMagicLink"
              class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200 disabled:opacity-50 mt-1"
            >
              {{ isSendingMagicLink ? 'Sending link...' : 'Email me a sign-in link' }}
            </Button>
            <p v-if="magicLinkError" class="text-sm text-red-500">{{ magicLinkError }}</p>
          </div>
        </template>
        <template v-else>
          <div class="space-y-3 text-center">
            <h2 class="text-xl font-semibold text-gray-900">Check your inbox</h2>
            <p class="text-sm text-gray-600 leading-relaxed">
              We sent a sign-in link to <span class="font-medium text-gray-900">{{ sentMagicLinkEmail }}</span>.
            </p>
            <button
              type="button"
              class="text-sm text-indigo-600 hover:text-indigo-500"
              @click="magicLinkSent = false"
            >
              Use a different email
            </button>
          </div>
        </template>

        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-xs">
            <span class="px-3 bg-white text-gray-400 uppercase tracking-wider">Or continue with</span>
          </div>
        </div>

        <SocialLoginButtons />

        <div class="text-center text-sm text-gray-600">
          Don't have an account?
          <router-link to="/register" class="font-medium text-indigo-600 hover:text-indigo-500">
            Create account
          </router-link>
        </div>

        <div class="text-center text-xs text-gray-400">
          <router-link to="/forgot-password" class="text-gray-500 hover:text-gray-700 underline underline-offset-2">
            Use password reset
          </router-link>
          <span class="mx-1">&middot;</span>
          <span>For accounts that still use a password</span>
        </div>

        <p class="text-center text-xs text-gray-400 leading-relaxed">
          By continuing, you agree to our
          <router-link to="/terms-and-conditions" class="text-indigo-600 hover:text-indigo-500">Terms</router-link>
          and
          <router-link to="/privacy-policy" class="text-indigo-600 hover:text-indigo-500">Privacy Policy</router-link>
        </p>

        <div class="text-center text-xs text-gray-400 pt-1">
          Prefer email and password?
          <router-link to="/login/password" class="text-indigo-600 hover:text-indigo-500 font-medium">Sign in</router-link>
          <span class="mx-1">&middot;</span>
          <router-link to="/register" class="text-indigo-600 hover:text-indigo-500 font-medium">Create account</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import HyperspeedBackground from '@/components/layout/HyperspeedBackground.vue'
import SocialLoginButtons from '@/components/auth/SocialLoginButtons.vue'
import { requestMagicLink } from '@/services/magicLinkService'

const VISITED_COOKIE = 'tt_visited'

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

const isReturningVisitor = ref(!!getCookie(VISITED_COOKIE))
const greeting = ref(isReturningVisitor.value ? 'Welcome back' : 'Welcome')

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const magicLinkEmail = ref(typeof route.query.email === 'string' ? route.query.email : '')
const isSendingMagicLink = ref(false)
const magicLinkSent = ref(false)
const sentMagicLinkEmail = ref('')
const magicLinkError = ref<string | null>(null)

onMounted(async () => {
  setCookie(VISITED_COOKIE, '1', 365)

  const isAuthenticated = await authStore.checkAuth()
  if (isAuthenticated) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/')
  }
})

const sendMagicLink = async () => {
  if (!magicLinkEmail.value.trim()) return

  isSendingMagicLink.value = true
  magicLinkError.value = null

  try {
    await requestMagicLink(magicLinkEmail.value, `${window.location.origin}/app/magic-login`)
    sentMagicLinkEmail.value = magicLinkEmail.value.trim()
    magicLinkSent.value = true
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { error?: string; detail?: string | string[] } } }
    const raw = ax.response?.data?.error ?? ax.response?.data?.detail
    const msg = Array.isArray(raw) ? raw[0] : raw
    magicLinkError.value = typeof msg === 'string' ? msg : 'Could not send a sign-in link. Please try again.'
  } finally {
    isSendingMagicLink.value = false
  }
}
</script>

<style scoped>
.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}
</style>
